import { useState } from 'react';
import { useTeamEmail, type TeamEmail } from '../share/recipients/useTeamEmail';
import {
  MAX_TEAM_RECIPIENTS,
  useTeamRecipients,
  type TeamRecipients
} from '../share/recipients/useTeamRecipients';
import { sendTeamEmail, toSendRecipients } from '../share/sendTeamEmail';
import { useShareWithTeamMutation } from '../share/services/shareApi';
import { REVEAL_SEND_COPY, type NotifyBlock, type RevealEmail } from './notifyCopy';

export interface NotifyTeam {
  team: TeamRecipients;
  /** The email can be offered here. When false the box is unchecked and
   *  disabled, and `block` says why. */
  offered: boolean;
  /** The box is checked: it starts checked (AC), and is never checked
   *  while the email is not offered. */
  emailOn: boolean;
  setEmailOn: (on: boolean) => void;
  note: string;
  setNote: (note: string) => void;
  block: NotifyBlock | null;
  /** The confirm button may go ahead, as far as the email is concerned. */
  ready: boolean;
  /** Emails the selected members. Resolves with the outcome, never rejects. */
  send: () => Promise<RevealEmail>;
}

/**
 * The Make Visible dialog's Send Email Notification block (DV-3563): the
 * client's team with everyone selected, unlike Internal Share, and the
 * note. Mounted with the dialog, so each opening reads the team afresh and
 * starts over.
 *
 * The send is the same team send as Internal Share, made only after the
 * reveal succeeded (it refuses a hidden photoset): `usePhotosetVisibility`
 * calls `send` once the PATCH has answered.
 */
export const useNotifyTeam = (photosetId: string): NotifyTeam => {
  const team = useTeamRecipients(photosetId, 'all');
  const teamEmail = useTeamEmail();
  const [shareWithTeam] = useShareWithTeamMutation();
  const [wanted, setWanted] = useState(true);
  const [note, setNote] = useState('');

  const noTeam = team.list.status === 'ready' && team.members.length === 0;
  const offered = teamEmail !== 'off' && !noTeam;
  const emailOn = offered && wanted;
  const block = notifyBlock(teamEmail, noTeam, emailOn, team);
  const ready =
    !emailOn ||
    (teamEmail === 'on' && team.list.status === 'ready' && !team.refreshing && block === null);

  const send = async (): Promise<RevealEmail> => {
    const { picked } = team;
    const outcome = await sendTeamEmail({
      shareWithTeam,
      photosetId,
      picked,
      note,
      copy: REVEAL_SEND_COPY
    });
    return { outcome, recipients: toSendRecipients(picked) };
  };

  return {
    team,
    offered,
    emailOn,
    setEmailOn: setWanted,
    note,
    setNote,
    block,
    ready,
    send
  };
};

/** The one line the block gets, the reason the box is off first. */
const notifyBlock = (
  teamEmail: TeamEmail,
  noTeam: boolean,
  emailOn: boolean,
  team: TeamRecipients
): NotifyBlock | null => {
  if (teamEmail === 'off') return 'unavailable';
  if (noTeam) return 'no-team';
  if (!emailOn) return null;
  if (team.list.status === 'failed') return 'list-failed';
  if (team.list.status === 'loading') return null;
  if (team.picked.length === 0) return 'none-picked';
  if (team.picked.length > MAX_TEAM_RECIPIENTS) return 'too-many';
  return null;
};
